import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { Row } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { AppContext } from "../../../../context/app";
import { DialogModal, useDevice } from "@pushpress/shared-components";
import { Button, Dialog } from "@pushpress/pushpress-ui";
import { defaultToastOptions } from "../../../../utils/misc";
import { useSendMessage } from "../../api/hooks";
import { createPayload } from "../../helpers";
import { NO_REPLY_EMAIL } from "../../constants";
import SendMessageModalContent from "./SendMessageModalContent";
import {
  LeadRecordType,
  MemberRecordType,
  SendMessageFormValuesType,
  StaffRecordType,
  MESSAGE_TYPES,
} from "../../types";

type SendMessageModalProps = {
  rowsSelected: Row<MemberRecordType | LeadRecordType | StaffRecordType>[];
  selectAllRows: () => void;
  isAllRowsSelected: boolean;
};

export default function SendMessageModal({
  rowsSelected,
  selectAllRows,
  isAllRowsSelected,
}: SendMessageModalProps) {
  const { isMobile } = useDevice();
  const clearButtonText = isMobile ? "Clear" : "Clear selection";

  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const [isConfirmCloseVisible, setIsConfirmCloseVisible] = useState(false);

  const { client } = useContext(AppContext);
  const {
    control,
    handleSubmit,
    register,
    setError,
    clearErrors,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<SendMessageFormValuesType>({
    mode: "onTouched",
    defaultValues: {
      messageType: MESSAGE_TYPES.EMAIL,
      replyTo: client.email || NO_REPLY_EMAIL,
      bccMe: false,
      bccCoaches: false,
      bccAdmins: false,
      subject: "",
      emailText: "",
      smsMessage: "",
    },
  });

  const { mutate: sendMessage, isLoading } = useSendMessage();

  const handleSendMessage = (data: SendMessageFormValuesType) => {
    const { messageType, emailText, smsMessage } = data;

    if (
      messageType === MESSAGE_TYPES.EMAIL &&
      (emailText === "" || emailText === "<p></p>")
    ) {
      setError("emailText", { message: "Required" });
      return;
    }
    if (messageType === MESSAGE_TYPES.SMS && smsMessage === "") {
      setError("smsMessage", { message: "Required" });
      return;
    }

    if (messageType === MESSAGE_TYPES.SMS && smsMessage.length > 160) {
      setError("smsMessage", { message: "160 characters max" });
      return;
    }

    const payload = createPayload(
      data,
      rowsSelected.map((row) => row.original.userUuid),
    );

    if (!payload) {
      toast.error("No users selected", defaultToastOptions);
      return;
    }

    sendMessage(payload, {
      onSuccess: () => {
        toast.success("Message sent", defaultToastOptions);
        setIsEmailFormOpen(false);
        reset({
          messageType: MESSAGE_TYPES.EMAIL,
          replyTo: client.email || NO_REPLY_EMAIL,
          bccMe: false,
          bccCoaches: false,
          bccAdmins: false,
          subject: "",
          emailText: "",
          smsMessage: "",
        });
      },
      onError: () => {
        toast.error("Message not sent", defaultToastOptions);
      },
    });
  };

  const closeModal = () => {
    setIsEmailFormOpen(false);
    reset({
      messageType: MESSAGE_TYPES.EMAIL,
      replyTo: client.email || NO_REPLY_EMAIL,
      bccMe: false,
      bccCoaches: false,
      bccAdmins: false,
      subject: "",
      emailText: "",
      smsMessage: "",
    });
  };

  const handleModalClose = () => {
    if (isDirty) {
      setIsConfirmCloseVisible(true);
    } else {
      closeModal();
    }
  };

  const handleFieldsReset = () => {
    reset({
      ...watch(),
      emailText: "",
      smsMessage: "",
      subject: "",
      bccMe: false,
      bccCoaches: false,
      bccAdmins: false,
    });
  };

  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-2 max-sm:tw-gap-1 tw-ml-2 max-sm:tw-ml-1">
        <Button
          size={isMobile ? "sm" : "default"}
          variant="ghost"
          className="tw-text-primary"
          onClick={() => selectAllRows()}
        >
          {isAllRowsSelected ? clearButtonText : "Select all"}
        </Button>
        <Button
          size={isMobile ? "sm" : "default"}
          variant="outline"
          className="tw-text-neutral-800"
          onClick={() => setIsEmailFormOpen(true)}
        >
          Send message
        </Button>
      </div>
      <Dialog open={isEmailFormOpen} onOpenChange={handleModalClose} modal>
        <SendMessageModalContent
          rowsSelected={rowsSelected}
          handleSendMessage={handleSendMessage}
          companyName={client.company}
          companyEmail={client.email}
          watch={watch}
          isLoading={isLoading}
          register={register}
          clearErrors={clearErrors}
          setValue={setValue}
          errors={errors}
          control={control}
          handleSubmit={handleSubmit}
          handleFieldsReset={handleFieldsReset}
        />
      </Dialog>
      <DialogModal
        title="Discard unsaved changes?"
        visible={isConfirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={closeModal}
        secondaryHandler={() => setIsConfirmCloseVisible(false)}
        closeModal={() => setIsConfirmCloseVisible(false)}
      />
    </>
  );
}
